import React, { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import Tabs from '@Components/layout/Tabs/Tabs';
import SearchItemsContentLoader from '@Components/Loaders/SearchItemsContentLoader';
import SelectFilter from '@Components/Page/SelectFilter';
import { useAppKindContext } from '@Components/root';
import { MediaQueries } from '@Config/constants';
import { TabFeaturedType, useGetFeaturedTabAdvertsQuery } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';

import AdvertCard from '../AdvertCard';

import styles from './FeaturedTabAdverts.module.scss';

const tabs = [
  TabFeaturedType.DailyPromotion,
  TabFeaturedType.MostRecent,
  TabFeaturedType.MostPopular,
  TabFeaturedType.MostExpensive,
];

const defaultTab = 2;
const defaultFilter = TabFeaturedType.MostPopular;

type FeaturedTabAdvertsProps = {
  onCompleted: () => void;
};

const FeaturedTabAdverts = ({ onCompleted }: FeaturedTabAdvertsProps) => {
  const { appKind } = useAppKindContext();
  const t = useTranslations();
  const isTablet = useMediaQuery({ maxWidth: MediaQueries.tablet });

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [activeFilter, setActiveFilter] = useState(defaultFilter);

  const { data, loading } = useGetFeaturedTabAdvertsQuery({
    variables: {
      kind: appKind,
      tabFeaturedType: isTablet ? activeFilter : tabs[activeTab],
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted,
  });

  const titles = {
    [TabFeaturedType.DailyPromotion]: t('msg_featured_tab_daily_promotion'),
    [TabFeaturedType.MostRecent]: t('msg_featured_tab_most_recent'),
    [TabFeaturedType.MostPopular]: t('msg_featured_tab_most_popular'),
  };

  const tabTitles = Object.values(titles);

  const selectOptions = Object.entries(titles).map(([key, value]) => ({ value: key, label: value }));

  const adList = (
    <div className={styles.featuredItemContainer}>
      {(data?.tabFeaturedAds || []).map(item => (
        <AdvertCard key={item.id} advert={item} />
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      {isTablet ? (
        <>
          <SelectFilter
            onChange={setActiveFilter}
            options={selectOptions}
            defaultValue={defaultFilter}
            size="flex"
            className={styles.selectFilter}
            selectedValue={selectOptions.find(item => item.value === activeFilter)}
            localizeSelectLabels={false}
          />
          <div>{loading ? <SearchItemsContentLoader count={8} gridSize={8} /> : adList}</div>
        </>
      ) : (
        <Tabs
          justify="start"
          initialTab={defaultTab}
          backgroundColor="transparent"
          noShadow
          tabs={tabTitles}
          tabContents={<div>{loading ? <SearchItemsContentLoader count={8} gridSize={8} /> : adList}</div>}
          onTabChange={setActiveTab}
        />
      )}
    </div>
  );
};

export default FeaturedTabAdverts;
